import classNames from 'classnames';
import { ComponentProps, forwardRef, useCallback, useImperativeHandle, useMemo, useState } from 'react';
import setDenaliAttribute from '../../utils/setDenaliAttribute';
import FilterChip from '../FilterChip/FilterChip';
import SearchInput from '../SearchInput';
import FilterDropdown from './FilterDropdown';
import styles from './styles/styles.module.scss';
import { CustomFilterChip, FilterConfig } from './types';

// Imperative handle so consumers can drive the (internally-owned) open state — e.g. close the open
// dropdown in response to something only the consumer knows about, like the filter row being scrolled.
export type FilterBarHandle = {
  closeActiveFilter: () => void;
};

type FilterBarProps = {
  className?: string;
  filters: (CustomFilterChip | FilterConfig)[];
  filtersContainerClassName?: string;
  filterChipClassName?: string;
  id?: string;
  popupMenuContainerClassName?: string;
  onFilterClose?: (id: string) => void;
  onFilterOpen?: (id: string) => void;
  searchInput?: Pick<ComponentProps<typeof SearchInput>, 'onChange' | 'placeholder' | 'value'>;
  searchInputClassName?: string;
  truncateFilterChips?: boolean;
  usePortalForDropdowns?: boolean;
};

const FilterBar = forwardRef<FilterBarHandle, FilterBarProps>(function FilterBar(
  {
    className,
    filters,
    filtersContainerClassName,
    filterChipClassName,
    id,
    popupMenuContainerClassName,
    onFilterClose,
    onFilterOpen,
    searchInput,
    searchInputClassName,
    truncateFilterChips,
    usePortalForDropdowns
  },
  ref
) {
  const [openId, setOpenId] = useState<string>();

  const openFilter = useCallback(
    (id: string) => {
      onFilterOpen?.(id);
      setOpenId(id);
    },
    [onFilterOpen]
  );

  const closeFilter = useCallback(
    (id: string) => {
      onFilterClose?.(id);
      setOpenId(undefined);
    },
    [onFilterClose]
  );

  useImperativeHandle(
    ref,
    () => ({
      closeActiveFilter: () => {
        if (openId !== undefined) {
          closeFilter(openId);
        }
      }
    }),
    [openId, closeFilter]
  );

  const filterChips = useMemo(() => {
    return filters.map((filter, index) => {
      const key = `filter-${index}`;
      if (filter.type === 'custom-chip') {
        return <FilterChip className={filterChipClassName} {...filter.filterChip} key={key} size="sm" truncate={truncateFilterChips} />;
      }
      const isOpen = openId === filter.id;
      return (
        <FilterDropdown
          key={key}
          popupMenuContainerClassName={popupMenuContainerClassName}
          usePortal={usePortalForDropdowns}
          anchor={
            <FilterChip
              className={filterChipClassName}
              {...filter.filterChip}
              isOpen={isOpen}
              onClick={e => {
                filter.filterChip.onClick?.(e);
                if (isOpen) {
                  closeFilter(filter.id);
                } else {
                  openFilter(filter.id);
                }
              }}
              onClear={e => {
                filter.filterChip.onClear?.(e);
                closeFilter(filter.id);
              }}
              size="sm"
              truncate={truncateFilterChips}
            />
          }
          config={filter}
          isOpen={isOpen}
          onCloseRequest={() => {
            // Prevents open/close flash when one dropdown is closed by clicking on another dropdown
            setOpenId(currentId => {
              if (currentId === openId) {
                onFilterClose?.(filter.id);
                return undefined;
              }
              return currentId;
            });
          }}
        />
      );
    });
  }, [
    filters,
    openId,
    popupMenuContainerClassName,
    filterChipClassName,
    truncateFilterChips,
    closeFilter,
    openFilter,
    onFilterClose,
    usePortalForDropdowns
  ]);

  return (
    <div {...setDenaliAttribute('FilterBar')} id={id} className={classNames(styles.filterBar, className)}>
      {searchInput ? (
        <div className={classNames(styles.searchInputContainer, searchInputClassName)}>
          <SearchInput {...searchInput} className={styles.searchInput} size="sm" />
        </div>
      ) : null}
      <div className={classNames(styles.filters, filtersContainerClassName)}>{filterChips}</div>
    </div>
  );
});

export default FilterBar;
